import { z } from 'zod';
import { createModel, QueryMany, QueryOne } from '../../common';
import { dateSchema, idSchema, nameSchema, quantitySchema } from '../../validation';
import { connectionStatusSchema, connectionTagSchema } from './connection.enums';

/**
 * one party row inside a `Connection.parties[2]` tuple.
 *
 * SPECIAL CASE — this is the ONLY place in the schema where we do NOT
 * use the generic `idEntrySchema` `ids[]` array. Each id type is a
 * separate scalar string field so MongoDB indexes stay simple
 * (compound + single-multikey path: `{parties.orgId: 1, status: 1}`).
 *
 * ObjectId-referencing fields use `idSchema` (hex form, same as
 * `idEntrySchema.id`). `phone` / `email` stay plain `z.string()` because
 * they're natural strings, not hex refs.
 *
 * At least ONE field must be present — enforced at the app layer, not
 * the schema layer, because Zod tuple `.refine()` is awkward.
 *
 * `Driver` and `Contact` docs are NOT direct connection parties —
 * they're internal CRM records owned by a Client/Org. The first-class
 * "person/brand" identity that participates in a Connection is always
 * captured via {@link profileId} (Profile collection) or one of the
 * Org/Client/User ids.
 *
 * See ADR #15 in `docs/relations-v2/01-decisions.md` for rationale.
 */
export const connectionPartyEntrySchema = z.object({
  /** display name of this party (Org / Client / Profile / phone-only contact). */
  name: nameSchema,

  /** `Org` `_id` hex string. The dominant identifier when both parties are signed. */
  orgId: idSchema.optional(),
  /** `User` `_id` hex string. Used when an unsigned identity is a freelance individual. */
  userId: idSchema.optional(),
  /** Local `Client` doc `_id` hex string representing THIS party in this Connection's CRM. */
  clientId: idSchema.optional(),
  /**
   * `Profile` collection `_id` hex string — the global admin-curated brand
   * directory entry. Used so two Orgs don't store divergent copies of
   * "Acme Logistics" in their CRMs. See `docs/relations-v2/00-overview.md`.
   */
  profileId: idSchema.optional(),
  /** E.164 phone string when the party has no signed account yet. */
  phone: z.string().optional(),
  /** Email string when the party has no signed account yet. */
  email: z.string().optional(),

  /**
   * cross-CRM asymmetric reference. The `Client` doc in THIS party's
   * CRM that represents THE OTHER party of the Connection. Each side
   * may carry a different `remoteClientId` value (or none).
   */
  remoteClientId: idSchema.optional(),
});
export type ConnectionPartyEntry = z.infer<typeof connectionPartyEntrySchema>;

/**
 * `Connection db model` — bilateral relationship between two parties.
 *
 * Stores status (`pending` / `accepted` / `blocked`), counters
 * (`ordersCount`, `agreementsCount`, `lastOrderAt`, `lastAgreementAt`),
 * optional notes per side, and per-party tags (`preferred_carrier`,
 * `do_not_dispatch`, etc.).
 *
 * Symmetric — `parties[0]` and `parties[1]` are order-arbitrary; the
 * `ConnectionService.findOrCreate` flow handles dedup at the app
 * layer (no `pairKey`). A nightly `ConnectionDedupJob` merges any
 * duplicates created by writer races.
 *
 * Expected indexes (operator-applied, NOT created in code):
 * - `{ 'parties.orgId': 1, status: 1 }` — primary "my network" / dedup lookup
 * - `{ 'parties.clientId': 1 }` (sparse) — CRM-side lookup
 * - `{ 'parties.profileId': 1 }` (sparse) — Profile (brand directory) lookup
 *
 * See `docs/relations-v2/02-data-model.md` section 6 for the design
 * rationale; `docs/relations-v2/06-scenarios.md` for usage scenarios.
 */
export const ConnectionSchema = createModel({
  /** symmetric tuple. Order arbitrary. App-layer dedup via `ConnectionService.findOrCreate`. */
  parties: z.tuple([connectionPartyEntrySchema, connectionPartyEntrySchema]),

  status: connectionStatusSchema,

  /** when the Connection first became `accepted`. Absent while `pending` / `blocked`. */
  since: dateSchema.optional(),
  /** when the most recent Order involving these parties was created. */
  lastOrderAt: dateSchema.optional(),
  /** when the most recent Agreement between these parties was created. */
  lastAgreementAt: dateSchema.optional(),

  /** running count of Orders that linked these parties (bumped by `ConnectionService.bumpCounters`). */
  ordersCount: quantitySchema.default(0),
  /** running count of Agreements between these parties. */
  agreementsCount: quantitySchema.default(0),

  /** optional tags (preferred carrier, on-hold, archived, ...). */
  relationshipTags: z.array(connectionTagSchema).default([]),

  /**
   * free-text notes per side. `bySelf` is the viewer-side note; `byOther`
   * surfaces what the counter-party wrote. Stored verbatim from each
   * side's UI; not shared across sides automatically.
   */
  notes: z
    .object({
      bySelf: z.string().optional(),
      byOther: z.string().optional(),
    })
    .optional(),
});

/** `Connection db model` — bilateral relationship between two parties. */
export type Connection = z.infer<typeof ConnectionSchema.model>;
export type ConnectionData = z.infer<typeof ConnectionSchema.data>;
export type ConnectionPartial = z.infer<typeof ConnectionSchema.partial>;
export type ConnectionQueryMany = QueryMany<Connection>;
export type ConnectionQueryOne = QueryOne<Connection>;
